// src/pages/RealEstate.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatPrice, formatArea } from "../utils/format";
import RealEstateAgencyNotice from "../components/RealEstateAgencyNotice";

/**
 * 不動産（売買物件）一覧ページ
 * - API: GET /api/realestate?page=<n>&limit=<m>
 *   { data: [...], total: number }
 * - スマホ/PCで表示件数切替
 */
const RealEstate = () => {
  const mq = useMemo(() => window.matchMedia("(max-width: 767px)"), []);
  const initialPerPage = mq.matches ? 5 : 10;

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialPerPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const onChange = (e) => setItemsPerPage(e.matches ? 5 : 10);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mq]);

  const abortRef = useRef(null);

  useEffect(() => {
    async function fetchList() {
      setLoading(true);
      setError("");

      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const res = await fetch(
          `/api/realestate?page=${page}&limit=${itemsPerPage}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setList(Array.isArray(json.data) ? json.data : []);
        setTotal(typeof json.total === "number" ? json.total : 0);
      } catch (err) {
        if (err.name !== "AbortError") {
          setList([]);
          setTotal(0);
          setError("物件一覧の取得に失敗しました。時間をおいて再度お試しください。");
        }
      } finally {
        setLoading(false);
      }
    }
    fetchList();
  }, [page, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <h1 className="text-2xl font-bold mb-6 text-center">不動産事業（売買物件）</h1>

      {/* 導入文 */}
      <section className="mb-10 text-sm leading-7 text-gray-700">
        <p className="mb-2">
          当社では、<strong>河口湖</strong>・<strong>富士吉田市</strong>を中心に、
          富士山周辺エリアの売買物件（土地・戸建・マンション等）をご紹介しております。
        </p>
        <p>
          本ページでは、販売中の物件情報を写真や価格とあわせて一覧でご確認いただけます。
          気になる物件は詳細ページよりお問い合わせください。
        </p>
        <div className="mt-4 rounded-md border border-gray-200 border-l-4 border-l-primary bg-gray-50 p-3 text-sm text-gray-700">
          <RealEstateAgencyNotice />
        </div>
      </section>

      {/* 読み込み・エラー・データなし */}
      {loading && <p className="text-center text-gray-600 mb-6">読み込み中・・・</p>}
      {!!error && <p className="text-center text-red-600 mb-6">{error}</p>}
      {!loading && !error && list.length === 0 && (
        <p className="text-center text-gray-600 mb-6">
          現在、公開中の物件情報はありません。/ 読み込み中・・・
        </p>
      )}

      {/* 一覧 */}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {list.map((item) => {
          const photo = item?.photos?.[0] || "";
          const name = item?.name || "物件名未設定";

          return (
            <li key={item.id} className="border rounded overflow-hidden hover:shadow-md transition">
              <Link
                to={`/businessContent/real-estate/${item.id}`}
                className="group block"
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={`${name}の物件写真`}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-gray-400">
                    画像準備中
                  </div>
                )}
                <div className="p-4">
                  <h2 className="text-xl font-semibold">{name}</h2>
                  {item?.price && (
                    <p className="text-lg font-bold text-red-600 mt-1">
                      {formatPrice(item.price, item.taxIncluded !== false)}
                    </p>
                  )}
                  {(item?.propertyType || item?.address) && (
                    <p className="text-sm text-gray-600 mt-1">
                      {item.propertyType}
                      {item.propertyType && item.address ? "／" : ""}
                      {item.address}
                    </p>
                  )}
                  <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                    <div>土地面積：{formatArea(item.landArea) || "-"}</div>
                    <div>建物面積：{formatArea(item.buildingArea) || "-"}</div>
                    <div>築年：{item.buildYear || "-"}</div>
                    <div>交通：{item.transport || "-"}</div>
                  </div>
                  <span className="inline-block mt-2 text-blue-600 group-hover:underline">
                    詳細を見る →
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      {/* ページャー */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center items-center gap-4">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1 || loading}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            ‹ 前へ
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page === totalPages || loading}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            次へ ›
          </button>
        </div>
      )}

      {/* まとめ文 */}
      <section className="mt-12 text-sm leading-7 text-gray-700">
        <p className="mb-2">物件のご購入・売却のご相談も承っております。</p>
        <p>
          掲載物件以外のご希望条件がございましたら、
          お気軽にお問い合わせください。
        </p>
      </section>
    </div>
  );
};

export default RealEstate;
